import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import API_BASE, { getCsrfToken, withApiCredentials } from "../../../api";
import { getActiveAwareAuthToken } from "../../../auth/bootstrapTokens";

export default function useCatchConsentFlow({
  onUserUpdated,
  getCurrentSelectionId,
}) {
  const { t } = useTranslation();
  const [showExifConsent, setShowExifConsent] = useState(false);
  const [showLocationConsent, setShowLocationConsent] = useState(false);
  const [pendingExifUpload, setPendingExifUpload] = useState(null);
  const [isSavingExifConsent, setIsSavingExifConsent] = useState(false);
  const [isSavingLocationConsent, setIsSavingLocationConsent] = useState(false);
  const [exifConsentError, setExifConsentError] = useState("");
  const [locationConsentError, setLocationConsentError] = useState("");

  const saveFeatureConsent = useCallback(
    async (consentType) => {
      const token = getActiveAwareAuthToken(window);
      if (!token && !getCsrfToken()) {
        throw new Error("missing_token");
      }

      const response = await fetch(
        `${API_BASE}/auth/accept-feature-consent`,
        withApiCredentials({
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ consentType }),
        })
      );

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || "save_failed");
      }

      onUserUpdated?.(data);
      return data;
    },
    [onUserUpdated]
  );

  const requestExifConsent = useCallback((payload) => {
    setPendingExifUpload(payload);
    setExifConsentError("");
    setShowExifConsent(true);
  }, []);

  const requestLocationConsent = useCallback(() => {
    setLocationConsentError("");
    setShowLocationConsent(true);
  }, []);

  const resetSelectionDerivedConsentState = useCallback(() => {
    setPendingExifUpload(null);
    setShowExifConsent(false);
    setExifConsentError("");
  }, []);

  const handleAcceptExifConsent = useCallback(
    async (applySelectedMedia) => {
      setIsSavingExifConsent(true);
      setExifConsentError("");

      try {
        await saveFeatureConsent("acceptExifProcessing");
        const pendingUpload = pendingExifUpload;
        setPendingExifUpload(null);
        setShowExifConsent(false);

        if (
          (pendingUpload?.snapshotBundle?.snapshotFile ||
            pendingUpload?.snapshotBundle?.originalFile) &&
          pendingUpload.selectionId === getCurrentSelectionId()
        ) {
          await applySelectedMedia(
            pendingUpload.snapshotBundle,
            pendingUpload.type,
            pendingUpload.selectionId
          );
        }
      } catch (err) {
        console.error("Failed to save EXIF consent:", err);
        setExifConsentError(
          "We could not save your image metadata consent right now. Please try again."
        );
      } finally {
        setIsSavingExifConsent(false);
      }
    },
    [getCurrentSelectionId, pendingExifUpload, saveFeatureConsent]
  );

  const handleCancelExifConsent = useCallback(() => {
    setPendingExifUpload(null);
    setExifConsentError("");
    setShowExifConsent(false);
  }, []);

  const handleAcceptLocationConsent = useCallback(
    async (handlePostSubmit) => {
      setIsSavingLocationConsent(true);
      setLocationConsentError("");

      try {
        await saveFeatureConsent("acceptLocationSharing");
        setShowLocationConsent(false);
        await handlePostSubmit({ skipLocationConsent: true });
      } catch (err) {
        console.error("Failed to save location consent:", err);
        setLocationConsentError(t("map_location_consent_save_failed"));
      } finally {
        setIsSavingLocationConsent(false);
      }
    },
    [saveFeatureConsent, t]
  );

  const handleCancelLocationConsent = useCallback(() => {
    setLocationConsentError("");
    setShowLocationConsent(false);
  }, []);

  return {
    showExifConsent,
    showLocationConsent,
    pendingExifUpload,
    isSavingExifConsent,
    isSavingLocationConsent,
    exifConsentError,
    locationConsentError,
    setExifConsentError,
    requestExifConsent,
    requestLocationConsent,
    resetSelectionDerivedConsentState,
    handleAcceptExifConsent,
    handleCancelExifConsent,
    handleAcceptLocationConsent,
    handleCancelLocationConsent,
  };
}
